import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import apiService from '../../../services/apiService';

// واجهة الرسم
interface Fee {
  id?: string;
  name: string;
  type: 'fixed' | 'percentage' | 'tax';
  value: number;  // قيمة ثابتة أو نسبة مئوية
}

// واجهة مجموعة الرسوم
interface FeeGroup {
  id?: string;
  _id?: string;  // إضافة دعم لـ _id من MongoDB
  name: string;
  description?: string;
  fees: Fee[];
}

// واجهة إعدادات الضريبة
interface TaxSettings {
  value: number;
  updatedAt?: string;
}

const DEFAULT_TAX_RATE = 15; // نسبة الضريبة الافتراضية 15%

const FeeGroupForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [diagnosticInfo, setDiagnosticInfo] = useState<string | null>(null);
  const [showDiagnosticDialog, setShowDiagnosticDialog] = useState<boolean>(false);
  const [taxRate, setTaxRate] = useState<number>(DEFAULT_TAX_RATE);
  const [isInvalidId, setIsInvalidId] = useState<boolean>(false);
  const [taxRateNotFound, setTaxRateNotFound] = useState<boolean>(false);
  // إضافة متغير لتتبع الحالة العامة للنموذج
  const [formState, setFormState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [redirectCountdown, setRedirectCountdown] = useState<number>(5);
  
  // إعادة تصميم كاملة للتعامل مع المعرف
  
    // طريقة آمنة للتعامل مع المعرف
    const safelyHandleId = () => {
      if (!isEditMode) return true; // في وضع الإنشاء لا نحتاج لمعرف
      
      if (!id) {
        console.error("معرف غير موجود في وضع التعديل");
        return false;
      }
      
      // سجل المعرف بالكامل للتشخيص
      console.log("معرف الطلب:", {
        id: id,
        type: typeof id,
        length: id.length,
        validFormat: /^[0-9a-fA-F]{24}$/.test(id)
      });
      
      return true;
    };
    
    // بمجرد تحميل المكون، نتحقق من المعرف مباشرة
    useEffect(() => {
      if (!safelyHandleId()) {
        setIsInvalidId(true);
        setError('معرف مجموعة الرسوم غير صالح أو مفقود');
        setLoading(false);
      }
    }, []);
    
    // تعديل آلية إعادة التوجيه لتكون أكثر فعالية
    useEffect(() => {
      // تفعيل إعادة التوجيه التلقائي عند وجود معرف "undefined"
      if (isInvalidId && id === "undefined") {
        console.log("تم اكتشاف معرف غير صالح (undefined)، بدء العد التنازلي للتوجيه التلقائي");
        
        // تحديث العداد كل ثانية
        const timer = setTimeout(() => {
          if (redirectCountdown > 1) {
            setRedirectCountdown(prev => prev - 1);
          } else {
            // إعادة التوجيه تلقائياً بعد انتهاء العد التنازلي
            console.log("انتهى العد التنازلي، جاري التوجيه إلى صفحة القائمة");
            navigate('/finance/fee-groups');
          }
        }, 1000);
        
        // تنظيف المؤقت عند إلغاء تحميل المكون
        return () => clearTimeout(timer);
      }
    }, [isInvalidId, redirectCountdown, navigate, id]);
  
  // حوار إضافة/تعديل رسم
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [currentFee, setCurrentFee] = useState<Fee | null>(null);
  const [editIndex, setEditIndex] = useState<number>(-1);
  const [isFeeLoading, setIsFeeLoading] = useState<boolean>(false);
  const [feeError, setFeeError] = useState<string | null>(null);
  
  // نموذج إنشاء/تعديل مجموعة الرسوم
  const formik = useFormik<FeeGroup>({
    initialValues: {
      name: '',
      description: '',
      fees: []
    },
    validationSchema: Yup.object({
      name: Yup.string().required('اسم مجموعة الرسوم مطلوب'),
      fees: Yup.array().min(1, 'يجب إضافة رسم واحد على الأقل')
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      
      try {
        // تحويل بنية البيانات لتتوافق مع نموذج السيرفر
        // مهم: لا نحتاج لتحديد branch أو createdBy، سيتم تعيينهم بواسطة السيرفر من req.user
        const transformedData = {
          name: values.name,
          description: values.description,
          isActive: true,
          fees: values.fees.map(fee => ({
            name: fee.type === 'tax' ? `ضريبة - ${fee.name}` : fee.name,
            // استخدام القيمة المدخلة من المستخدم مباشرة
            amount: fee.value,
            percentage: fee.type === 'percentage' || fee.type === 'tax',
            description: fee.type === 'tax' ? 'ضريبة تُحسب على مجموع الرسوم فقط' : '',
            isActive: true
          }))
        };
        
        if (isEditMode && id) {
          // تحديث مجموعة رسوم موجودة - استخدام المعرف الذي تم استلامه من الرابط
          console.log(`تحديث مجموعة الرسوم بالمعرف: ${id}`);
          await apiService.put(`/finance/fee-groups/${id}`, transformedData);
        } else {
          // إنشاء مجموعة رسوم جديدة
          await apiService.post('/finance/fee-groups', transformedData);
        }
        
        // العودة إلى قائمة مجموعات الرسوم
        navigate('/finance/fee-groups');
      } catch (error: any) {
        console.error('خطأ في حفظ مجموعة الرسوم:', error);
        
        // عرض رسائل خطأ التحقق من الصحة بشكل أفضل
        if (error.response?.data?.validationErrors) {
          const validationErrors = error.response.data.validationErrors;
          const errorMessages = Object.keys(validationErrors).map(
            key => `${key}: ${validationErrors[key]}`
          ).join('\n');
          
          setError(
            `${error.response.data.message}\n${errorMessages}`
          );
        } else {
          setError(error.response?.data?.message || 'حدث خطأ في حفظ مجموعة الرسوم');
        }
        
        setLoading(false);
      }
    }
  });
  
  // جلب بيانات مجموعة الرسوم في حالة التعديل
  useEffect(() => {
    const fetchFeeGroup = async () => {
      // إعادة تعيين الحالة
      setFormState('loading');
      setError(null);
      
      // التحقق من وضع التعديل وصحة المعرف
      if (!isEditMode) {
        // وضع إنشاء جديد - لا نحتاج لجلب بيانات
        setFormState('ready');
        setLoading(false);
        return;
      }
      // التعامل مع المشكلة المحددة: كلمة "undefined" كنص
      if (id === "undefined") {
        console.error('مشكلة محددة: المعرف هو النص "undefined"', { id });
        setIsInvalidId(true);
        setError('خطأ في إنشاء الرابط: يجب اختيار مجموعة رسوم صالحة من القائمة قبل النقر على زر التعديل');
        setFormState('error');
        setLoading(false);
        setRedirectCountdown(5); // بدء العد التنازلي للتوجيه التلقائي
        
        // تسجيل معلومات تشخيصية مفصلة
        console.log('معلومات تشخيصية لمشكلة undefined:', {
          url: window.location.href,
          referrer: document.referrer,
          pathname: window.location.pathname,
          search: window.location.search,
          history: window.history.length,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        });
        
        return;
      }
      
      // التعامل مع حالات المعرف غير الصالح الأخرى
      if (!id || id === 'null') {
        console.error('معرف غير صالح: المعرف فارغ أو null', { id });
        setIsInvalidId(true);
        setError('معرف مجموعة الرسوم غير صالح. تأكد من استخدام رابط صحيح من قائمة مجموعات الرسوم');
        setFormState('error');
        setLoading(false);
        return;
      }
      
      // التحقق من تنسيق المعرف (ObjectId) - بدون استخدام دالة isValidObjectId
      if (!/^[0-9a-fA-F]{24}$/.test(id)) {
        console.error('معرف غير صالح: تنسيق المعرف غير صحيح', { id });
        setIsInvalidId(true);
        setError('معرف مجموعة الرسوم غير صالح. تأكد من استخدام رابط صحيح من قائمة مجموعات الرسوم');
        setFormState('error');
        setLoading(false);
        return;
      }
      
      // المعرف يبدو صالح - نبدأ التحميل
      setLoading(true);
      
      try {
        console.log('جلب مجموعة الرسوم بالمعرف:', id);
        
        // محاولة جلب البيانات من API
        const response = await apiService.get<any>(`/finance/fee-groups/${id}`);
        
        // تسجيل الاستجابة للتشخيص
        console.log('استجابة API لطلب مجموعة الرسوم:', response);
        
        // التحقق من صحة الاستجابة قبل معالجتها
        if (!response || !response.fees || !Array.isArray(response.fees)) {
          console.error('استجابة غير صالحة:', response);
          throw new Error('تنسيق الاستجابة غير صالح');
        }
        
        // تحويل بيانات الرسوم من نموذج السيرفر إلى نموذج العميل
        const transformedFees = response.fees.map((fee: any) => ({
          id: fee._id || fee.id,  // دعم كلا النوعين من المعرفات
          name: fee.name,
          type: fee.percentage ? 'percentage' : 'fixed',
          value: fee.amount
        }));
        
        // البحث عن رسوم الضريبة وتحديد نوعها بناءً على الاسم فقط
        transformedFees.forEach((fee: any) => {
          if (fee.name.toLowerCase().includes('ضريبة') ||
              fee.name.toLowerCase().includes('tax')) {
            fee.type = 'tax';
          }
        });
        
        // تعيين قيم النموذج
        formik.setValues({
          name: response.name || '',
          description: response.description || '',
          fees: transformedFees
        });
        
        setLoading(false);
      } catch (error: any) {
        // إضافة معلومات تشخيصية مفصلة للمطور
        console.error('خطأ في جلب بيانات مجموعة الرسوم:', {
          error,
          statusCode: error.response?.status,
          message: error.message,
          name: error.name,
          data: error.response?.data,
          id: id
        });
        
        // تبسيط التعامل مع الخطأ تماماً
        let errorMessage;
        
        // حالة 1: خطأ الشبكة - لا يمكن الوصول للخادم
        if (!error.response) {
          errorMessage = 'تعذر الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت وإعادة المحاولة.';
          setFormState('error');
        }
        // حالة 2: خطأ 404 - المجموعة غير موجودة
        else if (error.response.status === 404) {
          errorMessage = 'مجموعة الرسوم غير موجودة أو تم حذفها';
          setIsInvalidId(true);
          setFormState('error');
        }
        // حالة 3: خطأ 400 - طلب غير صالح (يشمل أخطاء المعرف)
        else if (error.response.status === 400) {
          // نفترض أن أخطاء 400 مع تضمين كلمة ID هي أخطاء معرّف
          const errorResponseStr = JSON.stringify(error.response.data || '');
          if (errorResponseStr.toLowerCase().includes('id') ||
              errorResponseStr.toLowerCase().includes('معرف') ||
              errorResponseStr.toLowerCase().includes('objectid')) {
            errorMessage = 'معرف مجموعة الرسوم غير صالح. تأكد من استخدام رابط صحيح من قائمة مجموعات الرسوم';
            setIsInvalidId(true);
          } else {
            // طلب غير صالح آخر
            errorMessage = error.response.data?.message || 'بيانات الطلب غير صالحة';
          }
          setFormState('error');
        }
        // حالة 4: خطأ 500 - خطأ في الخادم
        else if (error.response.status === 500) {
          errorMessage = 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقاً';
          setFormState('error');
        }
        // حالة 5: أي خطأ آخر
        else {
          errorMessage = error.response.data?.message || 'حدث خطأ أثناء جلب بيانات مجموعة الرسوم';
          setFormState('error');
        }
        
        // إعلام المستخدم بالخطأ
        setError(errorMessage);
        setLoading(false);
      }
    };
    
    // جلب نسبة الضريبة من الإعدادات
    const fetchTaxRate = async () => {
      try {
        try {
          const settings = await apiService.get<TaxSettings>('/finance/settings/tax-rate');
          if (settings && typeof settings.value === 'number') {
            setTaxRate(settings.value);
            setTaxRateNotFound(false);
          }
        } catch (error: any) {
          // التحقق من نوع الخطأ (404 يعني أن الإعدادات غير متوفرة)
          if (error.response && error.response.status === 404) {
            console.warn('إعدادات الضريبة غير متوفرة، سيتم استخدام القيمة الافتراضية:', DEFAULT_TAX_RATE);
            // تعيين العلم للإشارة إلى أن الضريبة غير متوفرة
            setTaxRateNotFound(true);
          } else {
            console.error('خطأ في جلب نسبة الضريبة:', error);
            setTaxRateNotFound(false);
          }
        }
      } catch (error) {
        console.error('حدث خطأ غير متوقع:', error);
        // استخدام القيمة الافتراضية في حالة الخطأ
        setTaxRateNotFound(false);
      }
    };
    
    fetchTaxRate();
    fetchFeeGroup();
  }, [id, isEditMode]);
  
  // نموذج إضافة/تعديل رسم
  const feeFormik = useFormik<Fee>({
    initialValues: {
      name: '',
      type: 'fixed',
      value: 0
    },
    validationSchema: Yup.object({
      name: Yup.string().required('اسم الرسم مطلوب'),
      type: Yup.string()
        .oneOf(['fixed', 'percentage', 'tax'], 'نوع الرسم غير صالح')
        .required('نوع الرسم مطلوب'),
      value: Yup.number()
        .required('قيمة الرسم مطلوبة')
        .min(0.01, 'يجب أن تكون القيمة أكبر من صفر')
    }),
    onSubmit: (values) => {
      setIsFeeLoading(true);
      setFeeError(null);
      
      try {
        // استخدام القيمة المدخلة من المستخدم مباشرة
        const feeToAdd: Fee = {
          ...values
        };
        
        console.log('إضافة رسم:', feeToAdd);
        
        // في حالة التعديل
        if (editIndex >= 0) {
          const updatedFees = [...formik.values.fees];
          updatedFees[editIndex] = feeToAdd;
          formik.setFieldValue('fees', updatedFees);
        } else {
          // في حالة الإضافة
          formik.setFieldValue('fees', [...formik.values.fees, feeToAdd]);
        }
        
        // إغلاق الحوار وإعادة تعيين النموذج
        setIsDialogOpen(false);
        setCurrentFee(null);
        setEditIndex(-1);
        feeFormik.resetForm();
        setIsFeeLoading(false);
      } catch (error) {
        console.error('خطأ في إضافة الرسم:', error);
        setFeeError('حدث خطأ في إضافة الرسم');
        setIsFeeLoading(false);
      }
    }
  });
  
  // فتح حوار إضافة رسم جديد
  const handleAddFee = () => {
    setCurrentFee(null);
    setEditIndex(-1);
    feeFormik.resetForm();
    setIsDialogOpen(true);
  };
  
  // فتح حوار تعديل رسم موجود
  const handleEditFee = (fee: Fee, index: number) => {
    setCurrentFee(fee);
    setEditIndex(index);
    feeFormik.setValues({
      name: fee.name,
      type: fee.type,
      value: fee.value
    });
    setIsDialogOpen(true);
  };
  
  // حذف رسم من المجموعة
  const handleDeleteFee = (index: number) => {
    const updatedFees = [...formik.values.fees];
    updatedFees.splice(index, 1);
    formik.setFieldValue('fees', updatedFees);
  };
  
  // إغلاق حوار إضافة/تعديل الرسم
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setCurrentFee(null);
    setEditIndex(-1);
    feeFormik.resetForm();
    setFeeError(null);
  };
  
  // عرض نص نوع الرسم بالعربية
  const getFeeTypeText = (type: 'fixed' | 'percentage' | 'tax') => {
    switch (type) {
      case 'fixed': return 'ثابت';
      case 'percentage': return 'نسبة مئوية';
      case 'tax': return 'ضريبة';
      default: return '';
    }
  };
  
  // عرض قيمة الرسم بطريقة مناسبة حسب النوع
  const getFeeValueText = (fee: Fee) => {
    switch (fee.type) {
      case 'fixed': return `${fee.value} ريال`;
      case 'percentage': return `${fee.value}%`;
      case 'tax': return `${fee.value}%`;
      default: return '';
    }
  };
  
  // وظيفة جمع معلومات تشخيصية وفتح الحوار
  const collectDiagnosticInfo = () => {
    try {
      // جمع معلومات عن المعرف
      const idInfo = {
        id: id,
        type: typeof id,
        length: id?.length || 0,
        isNull: id === null,
        isUndefined: id === undefined,
        isEmpty: id === '',
        format: id && /^[0-9a-fA-F]{24}$/.test(id) ? 'صالح' : 'غير صالح'
      };
      
      // جمع معلومات عن حالة المكون
      const componentState = {
        isEditMode,
        isInvalidId,
        formState,
        loading,
        errorMessage: error
      };
      
      // جمع معلومات عن التنقل والرابط
      const navigationInfo = {
        currentUrl: window.location.href,
        referrer: document.referrer,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash
      };
      
      // إنشاء سلسلة نصية للمعلومات التشخيصية
      const diagnosticText = `
=== معلومات المعرف ===
المعرف: ${JSON.stringify(id)}
النوع: ${idInfo.type}
الطول: ${idInfo.length}
null: ${idInfo.isNull}
undefined: ${idInfo.isUndefined}
فارغ: ${idInfo.isEmpty}
تنسيق: ${idInfo.format}

=== حالة المكون ===
وضع التعديل: ${componentState.isEditMode}
المعرف غير صالح: ${componentState.isInvalidId}
حالة النموذج: ${componentState.formState}
جاري التحميل: ${componentState.loading}
رسالة الخطأ: ${componentState.errorMessage || 'لا يوجد'}

=== معلومات التنقل ===
الرابط الحالي: ${navigationInfo.currentUrl}
الصفحة المرجعية: ${navigationInfo.referrer || 'غير متوفر'}
مسار الرابط: ${navigationInfo.pathname}
معلمات البحث: ${navigationInfo.search}
      `;
      
      setDiagnosticInfo(diagnosticText);
      setShowDiagnosticDialog(true); // فتح حوار المعلومات التشخيصية
    } catch (err) {
      setDiagnosticInfo(`خطأ في جمع المعلومات التشخيصية: ${err}`);
      setShowDiagnosticDialog(true);
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* رأس الصفحة */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {isEditMode ? 'تعديل مجموعة رسوم' : 'إنشاء مجموعة رسوم جديدة'}
          </Typography>
          
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/finance/fee-groups')}
          >
            العودة للقائمة
          </Button>
        </Box>
        
        {/* عرض الأخطاء */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </Alert>
        )}
        
        {/* تنبيه محسن عند وجود مشكلة في المعرف - مع عد تنازلي للتوجيه التلقائي */}
        {isInvalidId && (
          <Alert
            severity="error"
            sx={{ mb: 3, p: 2, border: '1px solid #f44336' }}
          >
            <Typography variant="h6" color="error" fontWeight="bold">
              لا يمكن تحميل مجموعة الرسوم
            </Typography>
            
            <Typography variant="body1" sx={{ mt: 1, fontWeight: 'medium' }}>
              {id === "undefined"
                ? <>
                    تم استدعاء صفحة التعديل بدون تحديد معرف صالح. يجب اختيار مجموعة الرسوم من القائمة أولاً قبل النقر على زر التعديل.
                    {redirectCountdown > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          component="span"
                          color="primary.main"
                          fontWeight="bold"
                        >
                          سيتم توجيهك تلقائياً إلى قائمة مجموعات الرسوم خلال {redirectCountdown} ثواني...
                        </Typography>
                      </Box>
                    )}
                  </>
                : error && error.includes("غير موجودة")
                  ? "مجموعة الرسوم غير موجودة أو تم حذفها"
                  : "معرّف مجموعة الرسوم غير صالح"
              }
            </Typography>
            
            <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
              يرجى اتباع أحد الإجراءات التالية:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <li>التأكد من اختيار المجموعة من صفحة قائمة مجموعات الرسوم</li>
              <li>النقر على عنصر من القائمة أولاً ثم الضغط على زر التعديل</li>
              <li>تحديث الصفحة والمحاولة مرة أخرى</li>
              <li>الرجوع للقائمة وإعادة المحاولة من البداية</li>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/finance/fee-groups')}
                sx={{ flex: 1 }}
              >
                العودة لقائمة مجموعات الرسوم
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
                sx={{ flex: 1 }}
              >
                تحديث الصفحة
              </Button>
            </Box>
            
            {/* زر التشخيص للمطورين */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                size="small"
                color="inherit"
                onClick={collectDiagnosticInfo}
                sx={{ fontSize: '0.75rem' }}
              >
                عرض معلومات تشخيصية للمطورين
              </Button>
            </Box>
          </Alert>
        )}
        
        {/* إشعار حول إعدادات الضريبة */}
        {taxRateNotFound && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">
              <b>تنبيه:</b> لم يتم العثور على إعدادات الضريبة في النظام. سيتم استخدام نسبة الضريبة الافتراضية ({DEFAULT_TAX_RATE}%)
              لعناصر الضريبة في مجموعة الرسوم هذه.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              يمكنك متابعة إنشاء/تعديل مجموعة الرسوم بدون مشاكل.
            </Typography>
          </Alert>
        )}
        
        {/* حالة التحميل */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              {/* بيانات مجموعة الرسوم */}
              <Grid item xs={12}>
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 5px rgba(0,0,0,0.05)' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      بيانات مجموعة الرسوم
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          id="name"
                          name="name"
                          label="اسم مجموعة الرسوم"
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.name && Boolean(formik.errors.name)}
                          helperText={formik.touched.name && formik.errors.name}
                          required
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          id="description"
                          name="description"
                          label="وصف مجموعة الرسوم"
                          value={formik.values.description}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          multiline
                          rows={3}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* الرسوم في المجموعة */}
              <Grid item xs={12}>
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 5px rgba(0,0,0,0.05)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        الرسوم في المجموعة
                      </Typography>
                      
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddFee}
                        size="small"
                      >
                        إضافة رسم
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    {formik.touched.fees && formik.errors.fees && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {String(formik.errors.fees)}
                      </Alert>
                    )}
                    
                    {formik.values.fees.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>اسم الرسم</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>النوع</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>القيمة</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>إجراءات</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {formik.values.fees.map((fee, index) => (
                              <TableRow key={index}>
                                <TableCell>{fee.name}</TableCell>
                                <TableCell>{getFeeTypeText(fee.type)}</TableCell>
                                <TableCell>{getFeeValueText(fee)}</TableCell>
                                <TableCell align="center">
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={() => handleEditFee(fee, index)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleDeleteFee(index)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ 
                        p: 3, 
                        textAlign: 'center', 
                        borderRadius: 1, 
                        bgcolor: alpha(theme.palette.action.hover, 0.5)
                      }}>
                        <Typography variant="body2" color="text.secondary">
                          لم يتم إضافة أي رسوم بعد. اضغط على "إضافة رسم" لإضافة رسوم إلى المجموعة.
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ mt: 3 }}>
                      <Alert severity="info" icon={<InfoIcon />}>
                        <Typography variant="body2">
                          يمكنك إضافة عدة أنواع من الرسوم إلى المجموعة:
                        </Typography>
                        <ul style={{ marginTop: 4, marginBottom: 0, paddingInlineStart: 20 }}>
                          <li>
                            <Typography variant="body2">
                              <b>رسوم ثابتة:</b> مبلغ محدد يتم خصمه من إجمالي المبلغ
                            </Typography>
                          </li>
                          <li>
                            <Typography variant="body2">
                              <b>نسبة مئوية:</b> نسبة من إجمالي المبلغ
                            </Typography>
                          </li>
                          <li>
                            <Typography variant="body2">
                              <b>ضريبة:</b> تطبيق نسبة الضريبة المحددة على إجمالي الرسوم فقط (الرسوم الثابتة + الرسوم النسبية)
                            </Typography>
                          </li>
                        </ul>
                      </Alert>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* أزرار الحفظ والإلغاء */}
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => navigate('/finance/fee-groups')}
                  disabled={loading}
                >
                  إلغاء
                </Button>
                
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={loading}
                >
                  {isEditMode ? 'حفظ التعديلات' : 'إنشاء مجموعة الرسوم'}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Paper>
      
      {/* حوار إضافة/تعديل رسم */}
      <Dialog 
        open={isDialogOpen} 
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="xs"
      >
        <form onSubmit={feeFormik.handleSubmit}>
          <DialogTitle>
            {editIndex >= 0 ? 'تعديل رسم' : 'إضافة رسم جديد'}
          </DialogTitle>
          
          <DialogContent dividers>
            {feeError && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                onClose={() => setFeeError(null)}
              >
                {feeError}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label="اسم الرسم"
                  value={feeFormik.values.name}
                  onChange={feeFormik.handleChange}
                  onBlur={feeFormik.handleBlur}
                  error={feeFormik.touched.name && Boolean(feeFormik.errors.name)}
                  helperText={feeFormik.touched.name && feeFormik.errors.name}
                  required
                  autoFocus
                  margin="dense"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl 
                  component="fieldset" 
                  error={feeFormik.touched.type && Boolean(feeFormik.errors.type)}
                  required
                >
                  <FormLabel component="legend">نوع الرسم</FormLabel>
                  <RadioGroup
                    row
                    name="type"
                    value={feeFormik.values.type}
                    onChange={feeFormik.handleChange}
                  >
                    <FormControlLabel 
                      value="fixed" 
                      control={<Radio />} 
                      label="رسم ثابت" 
                    />
                    <FormControlLabel 
                      value="percentage" 
                      control={<Radio />} 
                      label="نسبة مئوية" 
                    />
                    <FormControlLabel 
                      value="tax" 
                      control={<Radio />} 
                      label="ضريبة" 
                    />
                  </RadioGroup>
                  {feeFormik.touched.type && feeFormik.errors.type && (
                    <FormHelperText error>{String(feeFormik.errors.type)}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              {feeFormik.values.type !== 'tax' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="value"
                    name="value"
                    label={feeFormik.values.type === 'fixed' ? 'المبلغ' : 'النسبة المئوية'}
                    type="number"
                    value={feeFormik.values.value}
                    onChange={feeFormik.handleChange}
                    onBlur={feeFormik.handleBlur}
                    error={feeFormik.touched.value && Boolean(feeFormik.errors.value)}
                    helperText={feeFormik.touched.value && feeFormik.errors.value}
                    required
                    InputProps={{
                      endAdornment: (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          {feeFormik.values.type === 'fixed' ? 'ريال' : '%'}
                        </Typography>
                      )
                    }}
                  />
                </Grid>
              )}
              
              {feeFormik.values.type === 'tax' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="value"
                    name="value"
                    label="نسبة الضريبة"
                    type="number"
                    value={feeFormik.values.value}
                    onChange={feeFormik.handleChange}
                    onBlur={feeFormik.handleBlur}
                    error={feeFormik.touched.value && Boolean(feeFormik.errors.value)}
                    helperText={feeFormik.touched.value && feeFormik.errors.value}
                    required
                    InputProps={{
                      endAdornment: (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          %
                        </Typography>
                      )
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseDialog} color="inherit">
              إلغاء
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              disabled={isFeeLoading}
              startIcon={isFeeLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {editIndex >= 0 ? 'حفظ التعديلات' : 'إضافة'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      {/* حوار معلومات التشخيص للمطورين */}
      <Dialog
        open={showDiagnosticDialog}
        onClose={() => setShowDiagnosticDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          معلومات تشخيصية لمشكلة المعرف
          <IconButton
            aria-label="close"
            onClick={() => setShowDiagnosticDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" color="error.main" gutterBottom>
            هذه المعلومات للمطورين فقط
          </Typography>
          <TextField
            multiline
            fullWidth
            variant="outlined"
            value={diagnosticInfo || ''}
            InputProps={{
              readOnly: true,
              style: { fontFamily: 'monospace', direction: 'ltr', fontSize: '0.85rem' }
            }}
            minRows={15}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (diagnosticInfo) {
                navigator.clipboard.writeText(diagnosticInfo)
                  .then(() => alert('تم نسخ المعلومات التشخيصية'))
                  .catch(err => console.error('فشل نسخ النص:', err));
              }
            }}
            color="primary"
          >
            نسخ إلى الحافظة
          </Button>
          <Button onClick={() => setShowDiagnosticDialog(false)} color="inherit">
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeeGroupForm;